import * as React from 'react'
import { Link, useNavigate } from 'react-router'
import { CalendarClock, Loader2 } from 'lucide-react'
import type { SeasonReminderDto } from '@lib/dto/season-reminder.dto'
import { SEASON_LABELS } from '@lib/enums/season.enum'
import { formatDate, formatUtcDate } from '@lib/helpers/format'
import { formatMoneyAmount } from '@lib/helpers/money'
import {
  useDismissSeasonReminder,
  useStartRebid,
} from '@/hooks/useSeasonReminders'
import { Button } from '@/components/ui/button'
import {
  COL_OPTIONAL,
  COL_SECONDARY,
  COL_STICKY,
  ROW,
  TABLE,
  TABLE_FRAME,
  TD,
  TD_RIGHT,
  TH,
  TH_RIGHT,
} from '@/components/ui/table-classes'
import { businessToday } from '@/helpers/businessDay'
import { errorMessage } from '@/helpers/logger'
import { cn } from '@/lib/utils'

/** "Mar 1", for a `YYYY-MM-DD` day: a calendar day, so read in UTC. */
function formatDay(day: string): string {
  return formatUtcDate(`${day}T00:00:00Z`, { month: 'short', day: 'numeric' })
}

/**
 * Whether a reminder's season has already started.
 *
 * `startsOn` is a calendar day. Today is the day in Lubbock, the same day the
 * API uses, so an evening there is not already the next day.
 */
function hasStarted(reminder: SeasonReminderDto): boolean {
  return reminder.startsOn <= businessToday()
}

/** One reminder, and the two things to do about it. */
function ReminderRow({ reminder }: { reminder: SeasonReminderDto }) {
  const navigate = useNavigate()
  const startRebid = useStartRebid()
  const dismiss = useDismissSeasonReminder()
  const [confirming, setConfirming] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const input = { versionId: reminder.versionId, season: reminder.season }
  const busy = startRebid.isPending || dismiss.isPending

  async function handleRebid(): Promise<void> {
    setError(null)
    try {
      const { proposalId } = await startRebid.mutateAsync(input)
      void navigate(`/proposals/${proposalId}`)
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be done.'))
    }
  }

  async function handleDismiss(): Promise<void> {
    setError(null)
    try {
      await dismiss.mutateAsync(input)
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be done.'))
    }
  }

  return (
    <tr className={cn(ROW, 'border-b border-default last:border-b-0')}>
      <td className={cn(TD, COL_STICKY)}>
        <Link
          to={`/proposals/${reminder.proposalId}`}
          className="font-medium hover:text-brand hover:underline"
        >
          {reminder.clientName}
        </Link>
        <span className="block text-xs text-muted-text">
          {reminder.sites.map((site) => site.siteName).join(', ')}
        </span>
      </td>
      <td className={cn(TD, COL_SECONDARY, 'text-secondary-text')}>
        {reminder.proposalTitle}
      </td>
      <td className={TD}>
        {SEASON_LABELS[reminder.season]}
        <span className="block text-xs text-muted-text">
          {hasStarted(reminder) ? 'Started' : 'Starts'} {formatDay(reminder.startsOn)}
        </span>
      </td>
      <td className={cn(TD, COL_OPTIONAL, 'text-xs text-muted-text')}>
        {reminder.notifiedAt === null
          ? 'Not yet'
          : `${reminder.ownerName ?? 'Owner'}, ${formatDate(reminder.notifiedAt)}`}
      </td>
      <td className={cn(TD_RIGHT, 'tabular-nums')}>
        {reminder.estimate === null ? '—' : formatMoneyAmount(reminder.estimate)}
      </td>
      <td className={TD_RIGHT}>
        <div className="flex items-center justify-end gap-1.5">
          {confirming ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setConfirming(false)
                }}
              >
                Keep
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={() => {
                  void handleDismiss()
                }}
              >
                {dismiss.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Dismiss
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setConfirming(true)
                }}
              >
                Dismiss
              </Button>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => {
                  void handleRebid()
                }}
              >
                {startRebid.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : null}
                Start rebid
              </Button>
            </>
          )}
        </div>
        {error === null ? null : (
          <p className="mt-1 text-xs whitespace-normal text-danger">{error}</p>
        )}
      </td>
    </tr>
  )
}

/**
 * Work accepted proposals estimated for a season that is close
 * (docs/seasonal-work.md). Each is rebid as a new proposal for that season's
 * work, or dismissed when the client has said no.
 */
export function SeasonReminderTable({
  reminders,
}: {
  reminders: SeasonReminderDto[]
}) {
  if (reminders.length === 0) return null

  return (
    <section className="space-y-2" aria-labelledby="coming-up-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2
          id="coming-up-heading"
          className="flex items-center gap-1.5 text-sm font-semibold"
        >
          <CalendarClock className="h-3.5 w-3.5 text-muted-text" />
          Seasons coming up
        </h2>
        <p className="text-xs text-muted-text">
          Estimates from accepted proposals, to rebid before the crew goes out
        </p>
      </div>
      <div className={TABLE_FRAME}>
        <table className={TABLE}>
          <thead>
            <tr className="border-b border-default text-secondary-text">
              <th className={cn(TH, COL_STICKY, 'bg-surface')}>Client</th>
              <th className={cn(TH, COL_SECONDARY)}>Proposal</th>
              <th className={TH}>Season</th>
              <th className={cn(TH, COL_OPTIONAL)}>Reminded</th>
              <th className={TH_RIGHT}>Estimate</th>
              <th className={TH_RIGHT}>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {reminders.map((reminder) => (
              <ReminderRow
                key={`${reminder.versionId}:${reminder.season}`}
                reminder={reminder}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
